import { iLine, iOrder } from "/app/src/models";
import { useTranslation } from "react-i18next";
import { iLineService, iOrderService } from "/app/src/services";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { buildParams } from "/app/src/helpers";
import Line from "./line";
import { useCallback } from "react";
import Box from "/app/src/components/generic/components/box";
import { Button, Chip } from "@heroui/react";
import { IconBuilder } from "/app/src/components/icons/IconBuilder";
import DateTime from "/app/src/components/generic/formatting/dateTime";
import { handlePromiseError } from "/app/src/helpers/api";
import DisabledWrapper from "/app/src/components/generic/components/disabledWrapper";

/**
 * Displays the details of a line in an order created through the Order Builder App
 */
export default function Lines({ order }: { order: iOrder }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: lines } = useQuery({
    queryKey: ["lines", { orderId: order.id }],
    queryFn: () => {
      return iLineService.getAll(buildParams({ orderId: order.id }));
    },
    initialData: { lines: [] },
    select: (data: { lines: iLine[] }) => {
      return data.lines;
    },
    enabled: order?.id !== undefined,
  });

  // Create function that refreshes the order lines
  const { mutateAsync: refreshLines } = useMutation({
    mutationFn: () => {
      return iOrderService.refreshOrder(order.id).then(handlePromiseError);
    },
    onSuccess: (response) => {
      queryClient.setQueryData(["lines", { orderId: order.id }], {
        lines: response?.order?.lines,
      });
      queryClient.setQueryData(["order", order.id], response);
    },
  });

  const handleRefresh = useCallback(async () => {
    await refreshLines();
  }, [refreshLines]);

  return (
    <>
      <Box className="!mb-[1px]">
        <div className="flex items-center justify-between space-x-4">
          <h2 className="text-[18px] !mb-0">{t("translation:order_lines")}</h2>
          <div className="flex items-center space-x-4">
            <Chip
              variant="flat"
              color="success"
              size="sm"
              startContent={
                <IconBuilder icon="Calendar" size={16} color="#000000" />
              }
            >
              Last Updated: <DateTime date={order?.lastUpdate} />
            </Chip>
            {order?.type === "Replenishment" && (
              <Button
                color="primary"
                radius="none"
                size="sm"
                className="text-[14px]"
                isDisabled={order.orderSent}
                onPress={handleRefresh}
                startContent={
                  <IconBuilder icon="Refresh" color="#ffffff" size={16} />
                }
              >
                {t("translation:refresh_order_lines")}
              </Button>
            )}
          </div>
        </div>
      </Box>
      <DisabledWrapper
        disabled={order.type === "Replenishment" || order.orderSent}
      >
        {lines.map((line) => (
          <Line
            line={line}
            key={line.id}
            isCountOrder={order.type === "Count"}
            disabled={order.type === "Replenishment" || order.orderSent}
            integrationId={order.integrationId}
          />
        ))}
      </DisabledWrapper>
    </>
  );
}
